import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UserLogin } from "../../models/user.model";
import { environment } from "src/environments/environment";
import {
  ToastController,
  ModalController,
  Platform,
  LoadingController,
  ActionSheetController
} from "@ionic/angular";
import { ModalCommentPage } from "src/app/components/modal-comment/modal-comment.page";
import { loadingProps } from "src/utils/util.functions";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

@Injectable({
  providedIn: "root"
})
export class UtilService {

  private readonly PlayStoreUrl = "https://play.google.com/store/apps/details?id=com.zellepay.zelle&hl=en&gl=US";
  private readonly AppStoreUrl = "https://apps.apple.com/us/app/zelle/id1260755201";
  private readonly AndroidPackageName = "com.zellepay.zelle";
  private readonly iOSBundleName = "zelle://";

  constructor(
    private http: HttpClient,
    private toastController: ToastController,
    private modalController: ModalController,
    private platform: Platform,
    private loadCtrl: LoadingController,
    private actionSheetController: ActionSheetController
  ) { }

  async showLoading() {
    loadingProps.mode = this.platform.is("ios") ? "ios" : "md";
    const loading = await this.loadCtrl.create(loadingProps);
    return await loading.present();
  }

  async openCashApp() {
    try {
      await this.showLoading();
      const platforms = this.platform.platforms();

      if (platforms.includes("mobile")) {
        if (Capacitor.isNativePlatform()) {
          if (platforms.includes("android")) {
            // On Android native, try to open zelle via URL scheme, fallback to Play Store
            try {
              await Browser.open({ url: "zelle://", windowName: "_system" });
            } catch {
              await Browser.open({ url: this.PlayStoreUrl, windowName: "_system" });
            }
            await this.loadCtrl.dismiss();
          } else {
            // iOS native - open via URL scheme or App Store
            try {
              await Browser.open({ url: this.iOSBundleName, windowName: "_system" });
            } catch {
              await Browser.open({ url: this.AppStoreUrl, windowName: "_system" });
            }
            await this.loadCtrl.dismiss();
          }
        } else {
          // PWA / mobile web
          if (platforms.includes("android")) {
            await Browser.open({ url: this.PlayStoreUrl, windowName: "_system" });
          } else {
            await Browser.open({ url: this.AppStoreUrl, windowName: "_system" });
          }
          await this.loadCtrl.dismiss();
        }
      } else {
        this.loadCtrl.dismiss();
        const actionSheet = await this.actionSheetController.create({
          header: 'Download Zelle App on',
          buttons: [{
            text: 'Android',
            icon: 'logo-android',
            handler: async () => {
              await this.showLoading();
              await Browser.open({ url: this.PlayStoreUrl, windowName: "_system" });
              return await this.loadCtrl.dismiss();
            }
          }, {
            text: 'iOS',
            icon: 'logo-apple',
            handler: async () => {
              await this.showLoading();
              await Browser.open({ url: this.AppStoreUrl, windowName: "_system" });
              return await this.loadCtrl.dismiss();
            }
          }]
        });
        await actionSheet.present();
      }
    } catch (error) {
      const platforms = this.platform.platforms();
      if (platforms.includes("android")) {
        await Browser.open({ url: this.PlayStoreUrl, windowName: "_system" });
      } else {
        await Browser.open({ url: this.AppStoreUrl, windowName: "_system" });
      }
      await this.loadCtrl.dismiss();
    }
  }
  public loginUser(credentials: UserLogin) {
    return this.http.post(
      `${environment.API_BASE}/api/account/login`,
      credentials
    );
  }

  async hideLoading() {
    await this.loadCtrl.dismiss();
  }

  async hideAllToast() {
    while (await this.toastController.getTop()) {
      await this.toastController.dismiss();
    }
  }

  async showToast(info: string) {
    const toast = await this.toastController.create({
      message: info,
      duration: 4000,
      mode: this.platform.is("ios") ? "ios" : "md",
      cssClass: "toast"
    });
    await this.hideAllToast();
    await toast.present();
  }
  async showToastLogin(info: string) {
    const toast = await this.toastController.create({
      message: info,
      duration: 2000,
      mode: this.platform.is("ios") ? "ios" : "md",
      cssClass: "toast"
    });
    await this.hideAllToast();
    await toast.present();
  }
  async showLongToast(info: string) {
    const toast = await this.toastController.create({
      message: info,
      buttons: [
        {
          text: 'Close',
          role: 'cancel'
        }
      ],
      duration: 60000,
      mode: this.platform.is("ios") ? "ios" : "md",
      cssClass: "toast"
    });
    await this.hideAllToast();
    await toast.present();
  }

  async openModalComment() {
    const modal = await this.modalController.create({
      component: ModalCommentPage,
      cssClass: "modal-overlay"
    });

    return await modal.present();
  }
}
